import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Phone, Loader2, ShieldCheck, Eye, EyeOff, ArrowLeft, ArrowRight, Mail } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

import { usePortalAuth } from '../PortalAuthContext';
import portalApi from '../lib/portalApi';
import { usePortalDestination } from '../PortalDestinationContext';
import { PortalIdentity, PortalDestinationPicker } from '../PortalIdentity';
import { parsePortalPath, destinationPath } from '../lib/portalPaths';
import { getRecentPharmacyIds } from '../lib/recentPharmacies';
import { PortalInlineAlert } from '../ui/PortalInlineAlert';
import { PortalMagicLinkForm } from './PortalMagicLinkForm';


export default function PortalLoginPage() {
  const nav = useNavigate();
  const location = useLocation();
  const { pharmacy, href, parsed, directory } = usePortalDestination();
  const processedSession = useRef(false);
  const processedMagic = useRef(false);
  const { patient, loading: authLoading, login, signup, requestOtp, verifyOtp, googleSession, forgotPassword, resetPassword, verifyMagicLink } = usePortalAuth();
  const returnPath = () => {
    const from = location.state?.from;
    const saved = from ? from.pathname + (from.search || '') : sessionStorage.getItem('portal_return_path');
    if (saved?.startsWith('/portal')) {
      const [path, search] = saved.split('?');
      const target = parsePortalPath(path, search || '');
      if (!target.invalid && target.key === parsed.key && target.page !== 'login') return saved;
    }
    return href();
  };

  const [pharmacies, setPharmacies] = useState([]);
  const [mode, setMode] = useState('login'); // login | signup | forgot | magic
  const altFlow = mode === 'forgot' || mode === 'magic';
  const [channel, setChannel] = useState('email'); // email | phone — only relevant for login/signup
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // "Continue to [last pharmacy]" shortcut on the generic /portal/login (no
  // pharmacy in the link) — only when this device has a usable recent one.
  const lastPharmacy = useMemo(() => {
    if (pharmacy) return null;
    for (const id of getRecentPharmacyIds()) {
      const match = directory.find(p => p.tenant_id === id);
      if (match) return match;
    }
    return null;
  }, [pharmacy, directory]);

  useEffect(() => {
    if (patient && !authLoading && !loading && !location.hash.includes('session_id=')) {
      nav(returnPath(), { replace: true });
    }
    // The destination is validated by returnPath; never replace it with a generic home redirect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient, authLoading, loading, location.hash]);

  // Google OAuth callback: capture session_id from URL hash
  useEffect(() => {
    const hash = location.hash || '';
    if (hash.includes('session_id=') && !processedSession.current) {
      const sid = new URLSearchParams(hash.replace('#', '?')).get('session_id');
      if (sid) {
        processedSession.current = true;
        setLoading(true);
        googleSession(sid)
          .then(() => {
            toast.success('Signed in with Google');
            const next = returnPath();
            sessionStorage.removeItem('portal_return_path');
            nav(next, { replace: true });
          })
          .catch((e) => {
            toast.error(e?.response?.data?.detail || 'Google sign-in failed');
            nav(href('login'), { replace: true });
          })
          .finally(() => setLoading(false));
      }
    }
  // One-time exchange; the URL destination remains validated by the portal resolver.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  // Email sign-in link: exchange the one-use token, then drop it from the URL.
  useEffect(() => {
    const token = new URLSearchParams(location.search).get('magic');
    if (!token || processedMagic.current) return;
    processedMagic.current = true;
    setLoading(true);
    verifyMagicLink(token)
      .then(() => { toast.success('Signed in with your email link'); nav(returnPath(), { replace: true }); })
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        setAuthError(typeof detail === 'string' ? detail : 'This sign-in link could not be used');
        nav(href('login'), { replace: true });
      })
      .finally(() => setLoading(false));
  // One-time exchange of the emailed token.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    portalApi.get('/pharmacies').then((r) => setPharmacies(r.data.pharmacies || [])).catch(() => {});
  }, []);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerifiedPhone, setOtpVerifiedPhone] = useState('');
  const [preferredTenant, setPreferredTenant] = useState('');

  // forgot-password sub-flow
  const [forgotStep, setForgotStep] = useState('request'); // request | confirm
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const switchMode = (next) => {
    setMode(next);
    setAuthError('');
    setChannel('email');
    if (next !== 'forgot') setForgotStep('request');
  };

  const startGoogle = () => {
    sessionStorage.setItem('portal_return_path', returnPath());
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + href('login');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError('');
    try {
      if (mode === 'login') {
        await login({ email, password });
        toast.success('Welcome back!');
      } else {
        if (!name.trim()) throw new Error('Please enter your name');
        if (password.length < 8) throw new Error('Password must be at least 8 characters');
        await signup({ name, email, password, preferred_tenant_id: preferredTenant || null });
        toast.success('Account created — welcome!');
      }
      nav(returnPath(), { replace: true });
    } catch (err) {
      const message = err?.response?.data?.detail || err.message || 'Sign-in failed';
      setAuthError(typeof message === 'string' ? message : 'Sign-in failed');
      toast.error(typeof message === 'string' ? message : 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    setLoading(true);
    setAuthError('');
    try {
      const res = await requestOtp(phone);
      setOtpSent(true);
      if (res.sms_disabled) {
        toast.warning('SMS is disabled in this pharmacy — ask staff to enable it, or use email login.');
      } else {
        toast.success(`Code sent to ${res.phone}. Check your SMS.`);
      }
    } catch (err) {
      setAuthError(err?.response?.data?.detail || 'Could not send code');
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError('');
    try {
      await verifyOtp({
        phone, code: otp,
        name: mode === 'signup' ? name : undefined,
        preferred_tenant_id: preferredTenant || undefined,
      });
      toast.success('Signed in');
      nav(returnPath(), { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Backend signals "we need a name to finish signup"
      if (detail && typeof detail === 'object' && detail.code === 'signup_required') {
        setMode('signup');
        setOtpVerifiedPhone(detail.otp_verified_phone || phone);
        toast.info('Almost there — enter your name to finish signup.');
      } else {
        setAuthError(typeof detail === 'string' ? detail : 'Verification failed');
        toast.error(typeof detail === 'string' ? detail : 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const finishSignupWithVerifiedPhone = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError('');
    try {
      await signup({
        name, phone, otp_verified_phone: otpVerifiedPhone,
        preferred_tenant_id: preferredTenant || null,
      });
      toast.success('Account created — welcome!');
      nav(returnPath(), { replace: true });
    } catch (err) {
      setAuthError(err?.response?.data?.detail || 'Could not finish signup');
      toast.error(err?.response?.data?.detail || 'Could not finish signup');
    } finally {
      setLoading(false);
    }
  };

  const submitForgotRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError('');
    try {
      await forgotPassword(email);
      setForgotStep('confirm');
      toast.success('If an account exists for that email, a reset code is on its way.');
    } catch (err) {
      setAuthError(err?.response?.data?.detail || 'Could not send reset code');
    } finally {
      setLoading(false);
    }
  };

  const submitForgotConfirm = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError('');
    try {
      await resetPassword({ email, code: resetCode, newPassword });
      toast.success('Password updated — you are signed in.');
      nav(returnPath(), { replace: true });
    } catch (err) {
      setAuthError(err?.response?.data?.detail || 'Could not reset password');
    } finally {
      setLoading(false);
    }
  };

  const PasswordField = ({ id, testId, value, onChange, autoComplete, placeholder }) => (
    <div className="relative">
      <Input id={id} data-testid={testId} type={showPassword ? 'text' : 'password'} autoComplete={autoComplete}
        value={value} onChange={onChange} required placeholder={placeholder} className="pr-10" />
      <button type="button" onClick={() => setShowPassword((s) => !s)} data-testid={`${testId}-visibility-toggle`}
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
        {showPassword ? <EyeOff size={17} aria-hidden="true" /> : <Eye size={17} aria-hidden="true" />}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 portal-login-page">
      <div className="w-full max-w-md">
        <div className="mb-6 space-y-4">
          <PortalIdentity />
          {!pharmacy && lastPharmacy && (
            <Button type="button" variant="outline"
              className="w-full justify-between border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800"
              onClick={() => nav(destinationPath(lastPharmacy, null, 'login'))}
              data-testid="portal-continue-last-pharmacy">
              <span>Continue to {lastPharmacy.name}</span>
              <ArrowRight className="w-4 h-4 shrink-0" aria-hidden="true" />
            </Button>
          )}
          <PortalDestinationPicker />
        </div>

        <Card className="border-emerald-100 shadow-lg portal-login-card">
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-1 text-center" data-testid="portal-auth-heading">
              {mode === 'login' && <>
                <h1 className="text-2xl font-bold text-slate-900">Welcome back</h1>
                <p className="text-sm text-slate-500">Sign in to manage prescriptions and pharmacy services.</p>
              </>}
              {mode === 'signup' && <>
                <h1 className="text-2xl font-bold text-slate-900">Create your account</h1>
                <p className="text-sm text-slate-500">Sign up to manage prescriptions and pharmacy services.</p>
              </>}
              {mode === 'magic' && <>
                <h1 className="text-2xl font-bold text-slate-900">Sign in without a password</h1>
                <p className="text-sm text-slate-500">We'll email you a link that signs you straight in.</p>
              </>}
              {mode === 'forgot' && <>
                <h1 className="text-2xl font-bold text-slate-900">Reset your password</h1>
                <p className="text-sm text-slate-500">
                  {forgotStep === 'request' ? "We'll email you a code to reset your password." : 'Enter the code we sent and choose a new password.'}
                </p>
              </>}
            </div>

            {authError && <PortalInlineAlert status="danger" testId="portal-auth-error">{authError}</PortalInlineAlert>}

            {mode === 'signup' && !otpVerifiedPhone && (
              <div className="space-y-2">
                <Label htmlFor="portal-name">Your name</Label>
                <Input
                  id="portal-name" data-testid="portal-name-input"
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Ama Boateng" required
                />
              </div>
            )}

            {mode === 'signup' && !pharmacy && pharmacies.length > 0 && (
              <div className="space-y-2">
                <Label>Preferred pharmacy (optional)</Label>
                <Select value={preferredTenant} onValueChange={setPreferredTenant}>
                  <SelectTrigger data-testid="portal-preferred-pharmacy"><SelectValue placeholder="Choose a pharmacy" /></SelectTrigger>
                  <SelectContent>
                    {pharmacies.map((p) => (
                      <SelectItem key={p.tenant_id} value={p.tenant_id}>{p.brand_name || p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!altFlow && channel === 'email' && (
              <form onSubmit={submitEmail} className="space-y-3" data-testid="portal-email-form">
                <div className="space-y-2">
                  <Label htmlFor="portal-email">Email</Label>
                  <Input id="portal-email" data-testid="portal-email-input" type="email" autoComplete="email" value={email}
                    onChange={(e) => { setEmail(e.target.value); setAuthError(''); }} required placeholder="you@example.com" />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="portal-password">Password</Label>
                    {mode === 'login' && (
                      <button type="button" className="text-xs font-medium text-emerald-700 hover:underline"
                        data-testid="portal-forgot-password-link" onClick={() => switchMode('forgot')}>Forgot password?</button>
                    )}
                  </div>
                  <PasswordField id="portal-password" testId="portal-password-input" value={password}
                    onChange={(e) => { setPassword(e.target.value); setAuthError(''); }}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'} placeholder="At least 8 characters" />
                </div>
                <Button type="submit" data-testid="portal-submit-email" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'login' ? 'Sign in' : 'Create account')}
                </Button>
              </form>
            )}

            {!altFlow && channel === 'phone' && (
              !otpVerifiedPhone ? (
                <form onSubmit={submitOtp} className="space-y-3" data-testid="portal-phone-form">
                  <div className="space-y-2">
                    <Label htmlFor="portal-phone">Mobile number</Label>
                    <div className="flex gap-2">
                      <Input id="portal-phone" data-testid="portal-phone-input" value={phone}
                        onChange={(e) => { setPhone(e.target.value); setAuthError(''); }} required placeholder="0244 123 456" />
                      <Button type="button" data-testid="portal-send-otp" onClick={sendOtp} disabled={loading || !phone}>
                        {otpSent ? 'Resend' : 'Send code'}
                      </Button>
                    </div>
                  </div>
                  {otpSent && (
                    <div className="space-y-2">
                      <Label htmlFor="portal-otp">6-digit code</Label>
                      <Input id="portal-otp" data-testid="portal-otp-input" value={otp} maxLength={6}
                        onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '')); setAuthError(''); }} required placeholder="000000" />
                      <Button type="submit" data-testid="portal-verify-otp" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading || otp.length !== 6}>
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'login' ? 'Verify & sign in' : 'Verify & create account')}
                      </Button>
                    </div>
                  )}
                </form>
              ) : (
                <form onSubmit={finishSignupWithVerifiedPhone} className="space-y-3" data-testid="portal-finish-signup-form">
                  <PortalInlineAlert status="success" testId="portal-phone-verified-banner">
                    <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" aria-hidden="true" /> Phone verified — one last step.</span>
                  </PortalInlineAlert>
                  <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="portal-finish-signup" disabled={loading || !name.trim()}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Finish signup'}
                  </Button>
                </form>
              )
            )}

            {mode === 'login' && (
              <button type="button" className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-emerald-700 hover:underline"
                data-testid="portal-magic-link-option" onClick={() => switchMode('magic')}>
                <Mail size={12} aria-hidden="true" /> Email me a sign-in link instead
              </button>
            )}

            {mode === 'magic' && <PortalMagicLinkForm email={email} setEmail={setEmail} loginPath={href('login')} />}

            {!altFlow && !otpVerifiedPhone && (
              <button type="button" className="w-full flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:underline"
                data-testid="portal-channel-toggle" onClick={() => setChannel((c) => (c === 'email' ? 'phone' : 'email'))}>
                <Phone size={12} aria-hidden="true" /> {channel === 'email' ? 'Use phone number instead' : 'Use email instead'}
              </button>
            )}

            {mode === 'forgot' && forgotStep === 'request' && (
              <form onSubmit={submitForgotRequest} className="space-y-3" data-testid="portal-forgot-form">
                <div className="space-y-2">
                  <Label htmlFor="portal-forgot-email">Email</Label>
                  <Input id="portal-forgot-email" data-testid="portal-forgot-email-input" type="email" autoComplete="email"
                    value={email} onChange={(e) => { setEmail(e.target.value); setAuthError(''); }} required placeholder="you@example.com" />
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="portal-forgot-submit" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send reset code'}
                </Button>
              </form>
            )}

            {mode === 'forgot' && forgotStep === 'confirm' && (
              <form onSubmit={submitForgotConfirm} className="space-y-3" data-testid="portal-reset-form">
                <div className="space-y-2">
                  <Label htmlFor="portal-reset-code">6-digit code</Label>
                  <Input id="portal-reset-code" data-testid="portal-reset-code-input" value={resetCode} maxLength={6}
                    onChange={(e) => { setResetCode(e.target.value.replace(/\D/g, '')); setAuthError(''); }} required placeholder="000000" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="portal-new-password">New password</Label>
                  <PasswordField id="portal-new-password" testId="portal-new-password-input" value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setAuthError(''); }} autoComplete="new-password" placeholder="At least 8 characters" />
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="portal-reset-submit"
                  disabled={loading || resetCode.length !== 6 || newPassword.length < 8}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset password'}
                </Button>
                <button type="button" className="w-full text-xs text-center text-slate-500 hover:underline" data-testid="portal-forgot-resend"
                  onClick={submitForgotRequest} disabled={loading}>Resend code</button>
              </form>
            )}

            {altFlow && (
              <button type="button" className="w-full flex items-center justify-center gap-1 text-xs text-slate-500 hover:underline"
                data-testid="portal-forgot-back" onClick={() => switchMode('login')}>
                <ArrowLeft size={13} aria-hidden="true" /> Back to sign in
              </button>
            )}

            {!altFlow && <>
              <div className="relative flex items-center py-1">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="px-3 text-xs text-slate-400">or</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <Button
                type="button" variant="outline"
                onClick={startGoogle} data-testid="portal-google-btn"
                className="w-full border-slate-200 hover:bg-slate-50"
                disabled={loading}
              >
                <span className="mr-2">🇬</span> Continue with Google
              </Button>

              <button type="button" className="w-full text-sm text-center text-emerald-700 font-medium hover:underline"
                data-testid={mode === 'login' ? 'portal-create-account-link' : 'portal-back-to-login-link'}
                onClick={() => switchMode(mode === 'login' ? 'signup' : 'login')}>
                {mode === 'login' ? 'New here? Create account' : 'Already have an account? Sign in'}
              </button>
            </>}

            <p className="text-xs text-center text-slate-400">
              By continuing you agree to our terms & privacy notice.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
